// Story 9.4 (ADO-1948): the account order-history LIST read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for
// `GET /api/v1/orders`, the Story 9.4 "Previous orders" surface. Mirrors
// `audit-log.client.ts`: it shapes the query string and guards the response
// envelope, and it is the single consumer seam until the `openapi-typescript`
// generated client lands (Story 6.1 deviation D-1).
//
// The wire types here are HAND-WRITTEN rather than imported from
// `@shoplink/order-management`. apps/web imports no `@shoplink` module package at
// all (it is a pure API consumer), and the module barrel is a NestJS surface. They
// are kept in lock-step with
// `libs/modules/order-management/src/contracts/dtos/order-history.dto.ts` and
// `.../order-status.ts` by the runtime guards below: a contract change that this
// file has not tracked fails the guard and degrades to the screen's load-error
// banner, instead of white-screening on a field that is suddenly absent.
//
// The reads turn a non-2xx into a plain Error and the screen renders its banner. The send
// (Story 8.6, below) reads the RFC 7807 `detail` of a refusal, because that message is
// written for the Shopper.

import { localDayEndInstant, localDayStartInstant } from '../design-system';
import { parseBasketState, type BasketState } from './basket.client';
import type { OrderingEntryKind } from './ordering-choices.client';
import { API_PREFIX, apiFetch, PROBLEM_TYPE_HEADER } from './shoplink-api';

export const ORDERS_PATH = `${API_PREFIX}/orders`;

/** Money as the API serialises `@shoplink/domain` Money. */
export interface OrderMoney {
  readonly amount: number;
  readonly currency: string;
}

/**
 * Where a sent order has got to (`ORDER_STATUS_MODEL.md` §03). Exactly nine values:
 * the vocabulary is closed and the UI maps all nine, inventing no tenth.
 */
export type OrderStatus =
  | 'SUBMITTED'
  | 'SENT_TO_DEPOT'
  | 'PART_DISPATCHED'
  | 'DISPATCHED'
  | 'INVOICED'
  | 'COMPLETED'
  | 'NOT_SUPPLIED'
  | 'CANCELLED'
  | 'SEND_FAILED';

/** The runtime value list of {@link OrderStatus}, for the response guard. */
export const ORDER_STATUSES: readonly OrderStatus[] = [
  'SUBMITTED',
  'SENT_TO_DEPOT',
  'PART_DISPATCHED',
  'DISPATCHED',
  'INVOICED',
  'COMPLETED',
  'NOT_SUPPLIED',
  'CANCELLED',
  'SEND_FAILED',
];

/**
 * How the order was raised. A field of its own, never concatenated onto status.
 *
 * All SIX values of the backend vocabulary (`order-status.ts`, widened by Story 9.2),
 * not the two Enabler 9.0 shipped. Story 9.6 (ADO-1080), D-3: the guard below REJECTS
 * any origin outside this list and a rejected row fails the whole envelope, so while
 * this union said `'STANDARD' | 'PRESELL'` a single rep-placed (`REP`) or handheld
 * (`HHT`) order anywhere on the page blanked the entire order-history screen into its
 * load-error banner. That is a live defect independent of filtering; the AC3 "sales rep
 * orders" filter is simply the first feature that asks for such a row deliberately, so
 * widening it is a prerequisite rather than polish.
 */
export type OrderOrigin = 'STANDARD' | 'AUTO_GENERATED' | 'REP' | 'HHT' | 'PRESELL' | 'RED_ALERT';

const ORDER_ORIGINS: readonly OrderOrigin[] = [
  'STANDARD',
  'AUTO_GENERATED',
  'REP',
  'HHT',
  'PRESELL',
  'RED_ALERT',
];

/** One row of the order-history list (`OrderSummaryDto`). */
export interface OrderSummary {
  readonly orderId: string;
  /** The store's own reference, present only where the account captured one. */
  readonly customerOrderReference?: string;
  /** ShopLink's order number (`ORD-<n>`), omitted when the order has none. */
  readonly orderReference?: string;
  /** When the order was SENT, UTC-normalised ISO 8601. */
  readonly submittedAt: string;
  /** The Auth0 subject of the submitting user. The stable identity; not a name. */
  readonly placedByUserId: string;
  /**
   * The submitting user's name (AC3). Omitted when the subject does not resolve to
   * a live user in the caller's tenant, in which case the row falls back to
   * `placedByUserId` rather than claiming nobody sent the order.
   */
  readonly placedByName?: string;
  readonly depotCode: string;
  readonly status: OrderStatus;
  readonly origin: OrderOrigin;
  readonly total: OrderMoney;
  readonly depositTotal: OrderMoney;
  /**
   * Story 9.3 (ADO-1081), AC1 — whether this order is eligible for the external
   * "Track order" hand-off (order-intrinsic: NDC depot + submitted after the cutoff +
   * a resolvable reference). On the LIST row, not just the detail, because the control
   * renders on the card header and must be there with the accordion shut. The SPA also
   * hides it for supplier users (display-only); the endpoint is authoritative.
   * Always present.
   */
  readonly trackable: boolean;
}

/** "Page x of y" metadata (`OrderHistoryPageMetaDto`). */
export interface OrderHistoryPageMeta {
  readonly page: number;
  readonly pageSize: number;
  /** The count of the whole account-scoped set, not of this page. */
  readonly total: number;
  /** `max(1, ceil(total / pageSize))`. An empty account reads "Page 1 of 1". */
  readonly totalPages: number;
}

/** The order-history list response (`OrderHistoryPageDto`). */
export interface OrderHistoryPage {
  readonly orders: OrderSummary[];
  readonly page: OrderHistoryPageMeta;
}

/**
 * The orders the list can be read in: the route's `ORDER_HISTORY_SORT_OPTIONS`
 * allow-list, hand-written for the same reason as the wire types above. A value outside
 * it is a 400, so the screen never sends one.
 */
export const ORDER_HISTORY_SORTS = ['newest', 'oldest', 'totalDesc', 'totalAsc'] as const;

/** One of {@link ORDER_HISTORY_SORTS}. */
export type OrderHistorySort = (typeof ORDER_HISTORY_SORTS)[number];

/** The server's default order, newest first. Never sent on the wire. */
export const DEFAULT_ORDER_HISTORY_SORT: OrderHistorySort = 'newest';

/** Query for {@link listOrderHistory}. */
export interface OrderHistoryQuery {
  /** The active depot's uuid. Required: the list is depot-scoped. */
  readonly depotId: string;
  /**
   * Story 8.4 (ADO-2013), D-1/D-5: the Trading Account the Shopper chose there. It only selects
   * among the caller's own Customer's accounts (the server matches it with the Customer, and a
   * code that is not one of them reads as an empty page); omitted or `null` (staff, or no
   * account chosen), the server picks the Customer's account as before.
   */
  readonly customerAccountCode?: string | null;
  /** Enabler E8-G (ADO-3109): the cart's range, so the list shows that range's orders on their
   *  own (Chill and Fruit & Veg at 25, LSN and Better Deal at 88), as SL1.0 shows each
   *  environment's (`Orders<env>`). The entry kind goes as on cart calls, only when it is not
   *  `DEPOT`; for Fruit & Veg the server then leaves out orders with no recorded range, which
   *  belong to Chill's entry at the same depot and account (AC27). Omitted or `null`: every range
   *  at the depot, as before. */
  readonly range?: {
    readonly assortmentCode: string;
    readonly entryKind: OrderingEntryKind;
  } | null;
  /** 1-based page number. Omitted means the server's first page. */
  readonly page?: number;
  /**
   * Rows per page. Must be one of the sizes the route allow-lists; anything else
   * is a 400, which the screen surfaces as the load-error banner. Omitted means
   * the server default of 10.
   */
  readonly pageSize?: number;
  /**
   * The order the page is read in, applied server-side because the list is paged there.
   * Omitted, or the default `newest`, sends no `sort` parameter at all.
   */
  readonly sort?: OrderHistorySort;
  /**
   * Story 9.6 (ADO-1080) — the filter dimensions (AC1/AC2/AC3), each optional and each
   * OMITTED from the query string when absent. Omitting them all issues exactly the
   * Story 9.4 request, which is what AC5's "the full unfiltered list is restored"
   * means on the wire.
   *
   * Filtering is enforced SERVER-side (AC7): these narrow what the account-, depot- and
   * tenant-scoped read returns, and nothing here can widen that scope. No filter names an
   * account (the chosen account above only picks among the caller's own).
   */
  readonly filters?: OrderHistoryFilterQuery;
}

/**
 * The filter half of {@link OrderHistoryQuery}.
 *
 * The two date bounds are the calendar DAYS the user picked, `YYYY-MM-DD`, exactly as
 * the URL holds them. They are converted to INSTANTS here before they go on the wire —
 * see {@link listOrderHistory} — because a day is only a range once you say whose day it
 * is, and the answer is the viewer's, not UTC's.
 */
export interface OrderHistoryFilterQuery {
  /** Inclusive first submission day, `YYYY-MM-DD`, in the viewer's own zone. */
  readonly submittedFrom?: string;
  /** Inclusive last submission day, `YYYY-MM-DD`, in the viewer's own zone. */
  readonly submittedTo?: string;
  /** Case-insensitive substring of the store's or ShopLink's order reference. Empty means no filter. */
  readonly orderRef?: string;
  /** True narrows to rep-placed orders; false/absent is every origin (AC3). */
  readonly salesRepOnly?: boolean;
}

/**
 * Read a page of the account's sent orders, in `query.sort` order (newest first by
 * default).
 *
 * Throws on a non-2xx and on a body that does not match the contract, so the screen
 * shows a load-error banner. Guarding every ROW (not just the envelope) is what
 * keeps a drifted `status` or a stringified `amount` off the screen and out of the
 * currency formatter, where the amount would render as `NaN`.
 */
export async function listOrderHistory(query: OrderHistoryQuery): Promise<OrderHistoryPage> {
  const params = new URLSearchParams();
  params.set('depotId', query.depotId);
  const customerAccountCode = query.customerAccountCode ?? null;
  if (customerAccountCode !== null) params.set('customerAccountCode', customerAccountCode);
  const range = query.range ?? null;
  if (range !== null) {
    params.set('assortmentCode', range.assortmentCode);
    if (range.entryKind !== 'DEPOT') params.set('entryKind', range.entryKind);
  }
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
  if (query.sort !== undefined && query.sort !== DEFAULT_ORDER_HISTORY_SORT) {
    params.set('sort', query.sort);
  }
  // Story 9.6 (ADO-1080): each filter is set only when it is actually narrowing.
  // `salesRep=false` and an empty `orderRef` are dropped rather than sent, so a
  // cleared panel produces the Story 9.4 URL character for character — the server
  // would drop them anyway, but sending them would make every request look filtered
  // in the network log and in any future cache key.
  const filters = query.filters;
  if (filters !== undefined) {
    // The two date bounds go on the wire as INSTANTS, resolved from the picked day in
    // the VIEWER's zone: the start of that local day, and its last millisecond. The
    // route takes them as given and never re-interprets them, so the window the server
    // reads is exactly the window the reader sees on screen.
    //
    // This is the whole fix for a defect found against real data: the bounds used to be
    // sent as bare `YYYY-MM-DD` and widened server-side to the UTC day, so an order
    // stored at `2026-09-20T23:30Z` — which the list renders as `21/09/2026 01:30` at
    // UTC+2 — was excluded by a "from 21 September" filter. 22 of the demo account's
    // orders sat in that gap. A day is only a range once you say whose day it is.
    //
    // An unparseable day yields null and is simply not sent, so a malformed URL widens
    // the result set rather than 400-ing the screen (the URL layer already drops days
    // that are not real dates, so this is a second line, not the first).
    const from =
      filters.submittedFrom === undefined || filters.submittedFrom === ''
        ? null
        : localDayStartInstant(filters.submittedFrom);
    if (from !== null) params.set('submittedFrom', from);
    const to =
      filters.submittedTo === undefined || filters.submittedTo === ''
        ? null
        : localDayEndInstant(filters.submittedTo);
    if (to !== null) params.set('submittedTo', to);
    const orderRef = filters.orderRef?.trim() ?? '';
    if (orderRef !== '') params.set('orderRef', orderRef);
    if (filters.salesRepOnly === true) params.set('salesRep', 'true');
  }
  const res = await apiFetch(`${ORDERS_PATH}?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load order history (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isOrderHistoryPage(data)) throw new Error('Unexpected order-history response shape');
  return data;
}

// ── Story 9.5 (ADO-1949): the order-DETAIL read, for the expanded row ──────────────
//
// Same transport and same guard discipline as the list above. Kept in lock-step with
// `libs/modules/order-management/src/contracts/dtos/order-detail.dto.ts`. A 404 (a
// foreign, missing or malformed id, RFC 7807 on the wire) becomes a plain Error, so the
// accordion shows its own load-error state rather than white-screening.

/** One line of the expanded order (`OrderDetailLineDto`). */
export interface OrderDetailLine {
  readonly skuCode: string;
  /** The product's current name (looked up live; a product delisted since the order was
   *  sent still resolves to the name it was ordered under). Omitted when the SKU has no
   *  catalogue row at all, in which case the UI shows the code + a note. */
  readonly productName?: string;
  readonly quantity: number;
  readonly unitPrice: OrderMoney;
  /** The pre-discount list price where the line captured one; omitted otherwise. */
  readonly listPrice?: OrderMoney;
  readonly drsRate: OrderMoney;
  readonly lineDepositTotal: OrderMoney;
  readonly lineTotal: OrderMoney;
}

/** Story 8.4 (ADO-2013): the chosen account as a query string, or none. */
function accountQuery(customerAccountCode: string | null): string {
  return customerAccountCode === null
    ? ''
    : `?customerAccountCode=${encodeURIComponent(customerAccountCode)}`;
}

/** One sent order in full (`OrderDetailDto`). */
export interface OrderDetail {
  readonly orderId: string;
  readonly customerOrderReference?: string;
  readonly submittedAt: string;
  readonly placedByUserId: string;
  readonly placedByName?: string;
  readonly depotCode: string;
  readonly valueCentreCode?: string;
  readonly deliveryDate?: string;
  readonly status: OrderStatus;
  readonly origin: OrderOrigin;
  /**
   * Story 9.3 (ADO-1081), AC2 — whether Accord has invoiced the order, the one answer ShopLink can
   * give to "has it been delivered?". Always present.
   */
  readonly invoiced: boolean;
  /**
   * Story 9.3 (ADO-1081), AC1 — whether the order is eligible for the external "Track order"
   * hand-off (order-intrinsic: NDC depot + submitted after the cutoff + a resolvable reference). The
   * SPA also hides the control for supplier users (display-only); the endpoint is authoritative.
   * Always present.
   */
  readonly trackable: boolean;
  readonly total: OrderMoney;
  readonly depositTotal: OrderMoney;
  readonly lines: OrderDetailLine[];
}

/**
 * Read one sent order in full, for the expanded row.
 *
 * Throws on a non-2xx (a 404 for a foreign/missing/malformed id) and on a body that
 * does not match the contract, so the accordion shows its load-error state. The
 * `orderId` is path-encoded. Story 8.4 (ADO-2013), D-1/D-5: `customerAccountCode` is the
 * Trading Account the Shopper chose, sent so the detail follows it (an order of another
 * account is the same 404); `null` (staff, or none chosen) sends none and the server picks
 * the Customer's account as before.
 */
export async function getOrderDetail(
  orderId: string,
  customerAccountCode: string | null = null,
): Promise<OrderDetail> {
  const res = await apiFetch(
    `${ORDERS_PATH}/${encodeURIComponent(orderId)}${accountQuery(customerAccountCode)}`,
  );
  if (!res.ok) throw new Error(`Failed to load order detail (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isOrderDetail(data)) throw new Error('Unexpected order-detail response shape');
  return data;
}

function isOrderDetail(data: unknown): data is OrderDetail {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.orderId === 'string' &&
    (d.customerOrderReference === undefined || typeof d.customerOrderReference === 'string') &&
    typeof d.submittedAt === 'string' &&
    typeof d.placedByUserId === 'string' &&
    (d.placedByName === undefined || typeof d.placedByName === 'string') &&
    typeof d.depotCode === 'string' &&
    (d.valueCentreCode === undefined || typeof d.valueCentreCode === 'string') &&
    (d.deliveryDate === undefined || typeof d.deliveryDate === 'string') &&
    typeof d.status === 'string' &&
    (ORDER_STATUSES as readonly string[]).includes(d.status) &&
    typeof d.origin === 'string' &&
    (ORDER_ORIGINS as readonly string[]).includes(d.origin) &&
    typeof d.invoiced === 'boolean' &&
    typeof d.trackable === 'boolean' &&
    isMoney(d.total) &&
    isMoney(d.depositTotal) &&
    Array.isArray(d.lines) &&
    d.lines.every(isOrderDetailLine)
  );
}

// ── Story 9.3 (ADO-1081): the external tracking-URL read, for the "Track order" control ─────────
//
// GET /api/v1/orders/{orderId}/tracking-url → { trackingUrl }. The SPA calls this only when the
// detail says the order is trackable and the caller is not a supplier; the server is the
// authoritative gate and returns the generic 404 for any refusal, which becomes a plain Error here.

/** The external tracking deep link for one order (`OrderTrackingUrlDto`). */
export interface OrderTrackingUrl {
  readonly trackingUrl: string;
}

/**
 * Fetch the external `bwg.depot.guru` tracking URL for one order. The caller opens it in a new tab.
 * Throws on a non-2xx (a 404 when the order is not trackable, foreign, missing, or the caller is a
 * supplier) and on a body that does not match the contract, so the control can surface a transient
 * error rather than opening `about:blank`. `customerAccountCode` as for {@link getOrderDetail}.
 */
export async function getOrderTrackingUrl(
  orderId: string,
  customerAccountCode: string | null = null,
): Promise<OrderTrackingUrl> {
  const res = await apiFetch(
    `${ORDERS_PATH}/${encodeURIComponent(orderId)}/tracking-url${accountQuery(customerAccountCode)}`,
  );
  if (!res.ok) throw new Error(`Failed to load tracking link (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isOrderTrackingUrl(data)) throw new Error('Unexpected tracking-url response shape');
  return data;
}

function isOrderTrackingUrl(data: unknown): data is OrderTrackingUrl {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return typeof d.trackingUrl === 'string' && d.trackingUrl !== '';
}

function isOrderDetailLine(row: unknown): row is OrderDetailLine {
  if (typeof row !== 'object' || row === null) return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.skuCode === 'string' &&
    (r.productName === undefined || typeof r.productName === 'string') &&
    typeof r.quantity === 'number' &&
    Number.isFinite(r.quantity) &&
    isMoney(r.unitPrice) &&
    (r.listPrice === undefined || isMoney(r.listPrice)) &&
    isMoney(r.drsRate) &&
    isMoney(r.lineDepositTotal) &&
    isMoney(r.lineTotal)
  );
}

/** Response-envelope guard: `{ orders: [...], page: {...} }`. */
function isOrderHistoryPage(data: unknown): data is OrderHistoryPage {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  const envelope = data as { orders?: unknown; page?: unknown };
  if (!Array.isArray(envelope.orders)) return false;
  if (!envelope.orders.every(isOrderSummary)) return false;
  return isOrderHistoryPageMeta(envelope.page);
}

function isOrderHistoryPageMeta(meta: unknown): meta is OrderHistoryPageMeta {
  if (typeof meta !== 'object' || meta === null) return false;
  const m = meta as Record<string, unknown>;
  return (
    Number.isFinite(m.page) &&
    Number.isFinite(m.pageSize) &&
    Number.isFinite(m.total) &&
    Number.isFinite(m.totalPages)
  );
}

function isOrderSummary(row: unknown): row is OrderSummary {
  if (typeof row !== 'object' || row === null) return false;
  const r = row as Record<string, unknown>;
  return (
    typeof r.orderId === 'string' &&
    (r.customerOrderReference === undefined || typeof r.customerOrderReference === 'string') &&
    (r.orderReference === undefined || typeof r.orderReference === 'string') &&
    typeof r.submittedAt === 'string' &&
    typeof r.placedByUserId === 'string' &&
    (r.placedByName === undefined || typeof r.placedByName === 'string') &&
    typeof r.depotCode === 'string' &&
    typeof r.status === 'string' &&
    (ORDER_STATUSES as readonly string[]).includes(r.status) &&
    typeof r.origin === 'string' &&
    (ORDER_ORIGINS as readonly string[]).includes(r.origin) &&
    isMoney(r.total) &&
    isMoney(r.depositTotal) &&
    typeof r.trackable === 'boolean'
  );
}

function isMoney(value: unknown): value is OrderMoney {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.amount === 'number' && Number.isFinite(m.amount) && typeof m.currency === 'string'
  );
}

// ── Story 8.6 (ADO-2015): sending the order ───────────────────────────────────────────────────
//
//   POST /api/v1/orders   Idempotency-Key: <uuid>
//        { depotId, customerAccountCode?, entryKind?, cartId, customerOrderReference? }
//     → 201 (accepted now) | 200 (the same send again), both with the identical OrderConfirmation
//     → 4xx RFC 7807; the stale-cart 409s (`CART_CHANGED`, `EMPTY_CART`) also carry the header
//       `x-shoplink-problem-type: cart-changed` and the RFC 7807 `type`
//       https://shoplink.bwg.ie/errors/cart-changed
//
// A LOCK-STEP COPY of order-capture's `OrderConfirmationDto`; keep the two in sync. The guard below
// turns contract drift into an error rather than a half-rendered confirmation.

/** A cart line left out of the order because its product is no longer live. `name` is '' when
 *  the catalogue has none. */
export interface OmittedOrderLine {
  readonly skuCode: string;
  readonly name: string;
}

/** What the store is told once the order is accepted (AC2). */
export interface OrderConfirmation {
  readonly orderId: string;
  /** ShopLink's order number. */
  readonly orderReference: string;
  readonly submittedAt: string;
  readonly depotCode: string;
  readonly depotName: string;
  /** The order's depot is an NDC core depot. */
  readonly isNdcDepot: boolean;
  readonly accountName: string;
  readonly accountCode: string;
  /** The delivery day the order was accepted for (`YYYY-MM-DD`); absent when there is none. */
  readonly expectedDeliveryDate?: string;
  /** The store's own reference, echoed back when one was given. */
  readonly customerOrderReference?: string;
  /** Story 8.13 (ADO-2022), D-9/D-12: how the order reaches the store, when the cart offered the
   *  choice; absent when it offered none. */
  readonly fulfilmentMethod?: FulfilmentMethod;
  /** The delivery date the Shopper requested (`YYYY-MM-DD`), for a delivery that named one. */
  readonly deliveryDate?: string;
  /** The booked collection, for a collection. */
  readonly collection?: OrderCollection;
  /** The Shopper's notes for the depot, when given. */
  readonly specialInstructions?: string;
  /** The order total: the sent lines' locked prices, ex-DRS. */
  readonly total: OrderMoney;
  /** The deposit (DRS) total, kept apart from `total` and never added into it (SL1.0). */
  readonly depositTotal: OrderMoney;
  readonly omittedLines: readonly OmittedOrderLine[];
}

/** Story 8.13 (ADO-2022): how the order reaches the store (mirrors order-management's
 *  `FulfilmentMethod`). */
export type FulfilmentMethod = 'DELIVERY' | 'COLLECTION';

/** A booked collection: the branch's wall-clock date (`YYYY-MM-DD`) and slot (`HH:MM`), and who
 *  to ask for. */
export interface OrderCollection {
  readonly date: string;
  readonly time: string;
  readonly contactName: string;
}

/** Story 8.13 (ADO-2022), D-6: the fulfilment fields a send may carry; the server judges them. */
export interface FulfilmentRequestFields {
  readonly fulfilmentMethod?: FulfilmentMethod;
  readonly deliveryDate?: string;
  readonly collectionDate?: string;
  readonly collectionTime?: string;
  readonly contactName?: string;
  readonly specialInstructions?: string;
}

/** A field a fulfilment refusal is bound to. */
export type FulfilmentField = keyof FulfilmentRequestFields;

const FULFILMENT_FIELDS: ReadonlySet<string> = new Set<FulfilmentField>([
  'fulfilmentMethod',
  'deliveryDate',
  'collectionDate',
  'collectionTime',
  'contactName',
  'specialInstructions',
]);

/** One refused fulfilment field, with the server's message for it. */
export interface InvalidFulfilmentField {
  readonly field: FulfilmentField;
  readonly message: string;
}

export interface SubmitOrderRequest extends FulfilmentRequestFields {
  readonly depotId: string;
  /** Story 8.3 (ADO-2012), D-1: the Trading Account the cart is keyed by (Enabler E8-G,
   *  ADO-3109, DA-10: a staff user's picked store account too); absent with none chosen. */
  readonly customerAccountCode?: string;
  /** Enabler E8-G (ADO-3109), DA-8: the kind of the entry the cart belongs to; absent for a
   *  `DEPOT` entry, which the server reads as the default. */
  readonly entryKind?: OrderingEntryKind;
  readonly cartId: string;
  readonly customerOrderReference?: string;
  /** Story 8.3 (ADO-2012), Task 13.1 (card 09): the cart revision the Shopper reviewed. */
  readonly seenRevision?: number;
}

/**
 * The `x-shoplink-problem-type` slug the API sets on a 409 whose cart is not the server's current
 * cart (mirrors `apps/api` order-capture `CART_CHANGED_PROBLEM`): the cart changed, was sent
 * elsewhere, or is empty. Re-opening the depot's current cart is what fixes it; every other 409
 * carries no slug. Not a session-ending slug (`TERMINAL_PROBLEM_TYPES`).
 */
export const CART_CHANGED_PROBLEM_TYPE = 'cart-changed';

/**
 * The `x-shoplink-problem-type` slug the API sets on a 400 that refuses the customer order
 * reference (mirrors `apps/api` order-capture `CUSTOMER_ORDER_REFERENCE_PROBLEM`, Story 8.14):
 * one is required and none was given, it is over the length cap, or it holds a comma, a double
 * quote or a control character. Changing the reference is what fixes it, so the page shows the
 * refusal on the reference field. Not a session-ending slug (`TERMINAL_PROBLEM_TYPES`).
 */
export const CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE = 'customer-order-reference';

/**
 * The `x-shoplink-problem-type` slug the API sets on a 400 that refuses the fulfilment choice
 * (mirrors `apps/api` order-capture `ORDER_FULFILMENT_PROBLEM`, Story 8.13, D-6): a delivery date
 * no longer offered, a collection slot that has passed, a blank or over-long field. Its body
 * carries `invalidFields`, every refused field with its message, so the page shows each refusal
 * on its field. Not a session-ending slug (`TERMINAL_PROBLEM_TYPES`).
 */
export const ORDER_FULFILMENT_PROBLEM_TYPE = 'order-fulfilment';

/**
 * Story 8.3 (ADO-2012), Task 13.1 (card 09, D-24): the `x-shoplink-problem-type` slug of a send
 * refused because the cart changed while the Shopper reviewed it (mirrors order-capture's
 * `CART_CHANGED_WHILE_REVIEWING_PROBLEM`). The refusal carries the cart as it is now
 * ({@link OrderSubmissionRefusedError.changedCart}), so the page marks what changed.
 */
export const CART_CHANGED_WHILE_REVIEWING_PROBLEM_TYPE = 'cart-changed-while-reviewing';

/** The server refused the send (a 4xx other than a transient 408/425/429) with a message written
 *  for the Shopper. Retrying the same attempt unchanged cannot succeed. `problemType` is the
 *  response's `x-shoplink-problem-type` slug, when it carries one
 *  ({@link CART_CHANGED_PROBLEM_TYPE}, {@link CUSTOMER_ORDER_REFERENCE_PROBLEM_TYPE},
 *  {@link ORDER_FULFILMENT_PROBLEM_TYPE}). `invalidFields` is the fulfilment refusal's field list
 *  (empty for every other refusal). */
export class OrderSubmissionRefusedError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problemType: string | undefined = undefined,
    /** Story 8.3 (ADO-2012), card 09: with {@link CART_CHANGED_WHILE_REVIEWING_PROBLEM_TYPE},
     *  the cart as it is now. */
    readonly changedCart: BasketState | undefined = undefined,
    /** Story 8.13 (ADO-2022), D-6: with {@link ORDER_FULFILMENT_PROBLEM_TYPE}, every refused
     *  field with its message. */
    readonly invalidFields: readonly InvalidFulfilmentField[] = [],
  ) {
    super(message);
    this.name = 'OrderSubmissionRefusedError';
  }
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function isOmittedOrderLine(value: unknown): value is OmittedOrderLine {
  if (typeof value !== 'object' || value === null) return false;
  const line = value as Record<string, unknown>;
  return typeof line.skuCode === 'string' && typeof line.name === 'string';
}

/** A collection time as the slots are written, `HH:MM` (the options parser's check). */
const COLLECTION_TIME = /^\d{2}:\d{2}$/;

function isOrderCollection(value: unknown): value is OrderCollection {
  if (typeof value !== 'object' || value === null) return false;
  const collection = value as Record<string, unknown>;
  return (
    typeof collection.date === 'string' &&
    ISO_DAY.test(collection.date) &&
    typeof collection.time === 'string' &&
    COLLECTION_TIME.test(collection.time) &&
    typeof collection.contactName === 'string'
  );
}

/** The fulfilment fields of a confirmation, when present, are well formed (Story 8.13). */
function hasValidFulfilment(d: Record<string, unknown>): boolean {
  return (
    (d.fulfilmentMethod === undefined ||
      d.fulfilmentMethod === 'DELIVERY' ||
      d.fulfilmentMethod === 'COLLECTION') &&
    (d.deliveryDate === undefined ||
      (typeof d.deliveryDate === 'string' && ISO_DAY.test(d.deliveryDate))) &&
    (d.collection === undefined || isOrderCollection(d.collection)) &&
    (d.specialInstructions === undefined || typeof d.specialInstructions === 'string')
  );
}

/** Runtime guard for {@link OrderConfirmation}. */
export function isOrderConfirmation(data: unknown): data is OrderConfirmation {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.orderId === 'string' &&
    typeof d.orderReference === 'string' &&
    typeof d.submittedAt === 'string' &&
    !Number.isNaN(Date.parse(d.submittedAt)) &&
    typeof d.depotCode === 'string' &&
    typeof d.depotName === 'string' &&
    typeof d.isNdcDepot === 'boolean' &&
    typeof d.accountName === 'string' &&
    typeof d.accountCode === 'string' &&
    isMoney(d.total) &&
    isMoney(d.depositTotal) &&
    (d.expectedDeliveryDate === undefined ||
      (typeof d.expectedDeliveryDate === 'string' && ISO_DAY.test(d.expectedDeliveryDate))) &&
    (d.customerOrderReference === undefined || typeof d.customerOrderReference === 'string') &&
    hasValidFulfilment(d) &&
    Array.isArray(d.omittedLines) &&
    d.omittedLines.every(isOmittedOrderLine)
  );
}

const SUBMIT_REFUSED_FALLBACK = 'Your order could not be sent. Review your cart and try again.';

/** A refusal's Problem Details `detail`, its well-formed, known `invalidFields` entries and its
 *  `basket` member, read from the body once. A body that is not JSON gives none of them. */
async function refusalProblem(res: Response): Promise<{
  readonly detail: string | null;
  readonly invalidFields: readonly InvalidFulfilmentField[];
  /** The raw `basket` extension member (Story 8.3, ADO-2012, card 09), unparsed. */
  readonly basket: unknown;
}> {
  let body: { detail?: unknown; invalidFields?: unknown; basket?: unknown } | null;
  try {
    body = (await res.json()) as typeof body;
  } catch {
    return { detail: null, invalidFields: [], basket: undefined };
  }
  const detail = typeof body?.detail === 'string' && body.detail.length > 0 ? body.detail : null;
  const raw = Array.isArray(body?.invalidFields) ? (body.invalidFields as unknown[]) : [];
  const invalidFields = raw.flatMap((entry): InvalidFulfilmentField[] => {
    const candidate = entry as { field?: unknown; message?: unknown } | null;
    return typeof candidate?.field === 'string' &&
      FULFILMENT_FIELDS.has(candidate.field) &&
      typeof candidate.message === 'string'
      ? [{ field: candidate.field as FulfilmentField, message: candidate.message }]
      : [];
  });
  return { detail, invalidFields, basket: body?.basket };
}

/** Story 8.3 (ADO-2012), card 09: the cart a changed-while-reviewing refusal carries, or
 *  undefined when it carries none in the expected shape. */
function changedCartOf(basket: unknown): BasketState | undefined {
  try {
    return parseBasketState(basket);
  } catch {
    return undefined;
  }
}

/** The 4xx statuses that say "not now" rather than "not this": a request timeout (408), too
 *  early (425) and too many requests (429). The same attempt may succeed later, so they are
 *  thrown as a plain Error (retryable, key kept), never as a refusal. */
const TRANSIENT_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 425, 429]);

/**
 * Send the cart as an order, under `idempotencyKey` (one per attempt: a retry of the same attempt
 * reuses it, so the server answers with the first send's confirmation instead of a second order).
 * A 4xx is an {@link OrderSubmissionRefusedError} carrying the Problem Details `detail` and the
 * problem-type slug, if any; a 5xx, a transient 408/425/429, a network failure, an aborted
 * `signal` or a malformed body is a plain Error, which the caller may retry with the same key.
 */
export async function submitOrder(
  request: SubmitOrderRequest,
  idempotencyKey: string,
  options: { readonly signal?: AbortSignal } = {},
): Promise<OrderConfirmation> {
  const res = await apiFetch(ORDERS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(request),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (res.status >= 400 && res.status < 500 && !TRANSIENT_CLIENT_STATUSES.has(res.status)) {
    const problemType = res.headers.get(PROBLEM_TYPE_HEADER) ?? undefined;
    const problem = await refusalProblem(res);
    // Story 8.3 (ADO-2012), card 09: the cart as it is now.
    const changedCart =
      problemType === CART_CHANGED_WHILE_REVIEWING_PROBLEM_TYPE
        ? changedCartOf(problem.basket)
        : undefined;
    throw new OrderSubmissionRefusedError(
      problem.detail ?? SUBMIT_REFUSED_FALLBACK,
      res.status,
      problemType,
      changedCart,
      problemType === ORDER_FULFILMENT_PROBLEM_TYPE ? problem.invalidFields : [],
    );
  }
  if (!res.ok) throw new Error(`Failed to send the order (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isOrderConfirmation(data)) throw new Error('Unexpected order confirmation shape');
  return data;
}
